import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import { beneficiarySubscriptionService } from "../services/beneficiarySubscription.service";

export const fetchAllSubscriptions = createAsyncThunk(
  "beneficiarySubscription/fetchAll",
  async (_, thunkAPI) => {
    try {
      return await beneficiarySubscriptionService.getAllSubscriptions();
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to fetch subscriptions");
    }
  },
);

export const fetchSubscriptionsByCustomer = createAsyncThunk(
  "beneficiarySubscription/fetchByCustomer",
  async (customerId, thunkAPI) => {
    try {
      return await beneficiarySubscriptionService.getSubscriptionsByCustomer(
        customerId,
      );
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to fetch subscriptions");
    }
  },
);

export const fetchCustomerSubscriptions = createAsyncThunk(
  "beneficiarySubscription/fetchCustomer",
  async (customerId, { rejectWithValue }) => {
    try {
      return await beneficiarySubscriptionService.getCustomerSubscriptions(
        customerId,
      );
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Error");
    }
  },
);

export const addSubscription = createAsyncThunk(
  "beneficiarySubscription/add",
  async (subscription, thunkAPI) => {
    try {
      return await beneficiarySubscriptionService.createSubscription(
        subscription,
      );
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || "Failed to assign plan",
      );
    }
  },
);

export const editSubscription = createAsyncThunk(
  "beneficiarySubscription/edit",
  async ({ id, subscription }, thunkAPI) => {
    try {
      await beneficiarySubscriptionService.updateSubscription(id, subscription);

      return {
        beneficiarySubscriptionId: id,
        ...subscription,
      };
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to update subscription");
    }
  },
);

export const removeSubscription = createAsyncThunk(
  "beneficiarySubscription/remove",
  async (id, thunkAPI) => {
    try {
      await beneficiarySubscriptionService.deleteSubscription(id);
      return id;
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to delete subscription");
    }
  },
);

export const fetchSubscriptionByCodeBar = createAsyncThunk(
  "beneficiarySubscription/fetchByCodeBar",
  async (code, { rejectWithValue }) => {
    try {
      return await beneficiarySubscriptionService.getByCodeBar(code);
    } catch (error) {
      return rejectWithValue(
        error.response?.data || error.message || "Badge invalide",
      );
    }
  },
);

export const rechargeSubscription = createAsyncThunk(
  "beneficiarySubscription/recharge",
  async ({ id, rechargeData }, { rejectWithValue }) => {
    try {
      const response =
        await beneficiarySubscriptionService.rechargeSubscription(
          id,
          rechargeData,
        );
      return { id, result: response };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Échec de la recharge",
      );
    }
  },
);

const beneficiarySubscriptionSlice = createSlice({
  name: "beneficiarySubscription",

  initialState: {
    subscriptions: [],
    customerSubscriptions: [], // NEW
    selectedCustomerId: null,
    loading: false,
    error: null,
  },

  reducers: {
    setSelectedCustomer: (state, action) => {
      state.selectedCustomerId = action.payload;

      if (!action.payload) {
        state.subscriptions = [];
      }
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchAllSubscriptions.pending, (state) => {
        state.loading = true;
      })

      .addCase(fetchAllSubscriptions.fulfilled, (state, action) => {
        state.loading = false;
        state.subscriptions = action.payload;
      })

      .addCase(fetchAllSubscriptions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchSubscriptionsByCustomer.pending, (state) => {
        state.loading = true;
      })

      .addCase(fetchSubscriptionsByCustomer.fulfilled, (state, action) => {
        state.loading = false;
        state.subscriptions = action.payload;
      })

      .addCase(fetchSubscriptionsByCustomer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchCustomerSubscriptions.pending, (state) => {
        state.loading = true;
      })

      .addCase(fetchCustomerSubscriptions.fulfilled, (state, action) => {
        state.loading = false;
        state.customerSubscriptions = action.payload;
      })

      .addCase(fetchCustomerSubscriptions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(addSubscription.fulfilled, (state, action) => {
        state.subscriptions.push(action.payload);
      })

      .addCase(editSubscription.fulfilled, (state, action) => {
        const idx = state.subscriptions.findIndex(
          (s) =>
            s.beneficiarySubscriptionId ===
            action.payload.beneficiarySubscriptionId,
        );

        if (idx !== -1) {
          state.subscriptions[idx] = {
            ...state.subscriptions[idx],
            ...action.payload,
          };
        }
      })

      .addCase(removeSubscription.fulfilled, (state, action) => {
        state.subscriptions = state.subscriptions.filter(
          (s) => s.beneficiarySubscriptionId !== action.payload,
        );
      })
      .addCase(rechargeSubscription.pending, (state) => {
        state.loading = true;
      })
      .addCase(rechargeSubscription.fulfilled, (state, action) => {
        state.loading = false;
        const { id, result } = action.payload;

        // Mise à jour optimiste de l'abonnement dans la liste
        const subIndex = state.subscriptions.findIndex(
          (s) => s.beneficiarySubscriptionId === id,
        );

        if (subIndex !== -1 && result.recharge) {
          const sub = state.subscriptions[subIndex];
          const newRecharge = result.recharge;

          // 1. Mettre à jour le solde ou les crédits
          if (sub.subscriptionType === 0) {
            // ByPrice
            sub.balanceRemaining =
              (sub.balanceRemaining || 0) + (newRecharge.amountAdded || 0);
          } else {
            // ByMealCount
            if (sub.creditsRemaining < 2147483647) {
              sub.creditsRemaining += newRecharge.creditsAdded || 0;
            }
          }

          // 2. Ajouter la nouvelle recharge à l'historique local
          if (!sub.recharges) sub.recharges = [];
          sub.recharges.unshift(newRecharge); // Ajoute au début du tableau
        }
      })
      .addCase(rechargeSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setSelectedCustomer } = beneficiarySubscriptionSlice.actions;

export default beneficiarySubscriptionSlice.reducer;
